import { RevelMetricAttribution } from '../data/scorecardData';

/**
 * Decode emoji strings stored as "uD83EuDD69" (UTF-16 surrogate pairs as text)
 * into actual emoji characters. Returns the input unchanged if it's already a
 * normal string or emoji.
 */
export const parseEmojiString = (raw: string): string => {
  const codeUnits = raw.match(/u([0-9A-Fa-f]{4})/g);
  if (!codeUnits) return raw;
  const codes = codeUnits.map((u) => parseInt(u.slice(1), 16));
  return String.fromCharCode(...codes);
};

/**
 * Returns Tailwind color classes for a score value (0-100).
 * Thresholds: green >= 90, yellow >= 80, red < 80.
 */
export const getScoreColor = (score: number): string => {
  if (score >= 90) return 'text-green-600 bg-green-100';
  if (score >= 80) return 'text-yellow-600 bg-yellow-100';
  return 'text-red-600 bg-red-100';
};

/**
 * Returns Tailwind color classes for a metric percentage (0-100).
 * Thresholds: green >= 90, yellow >= 80, red < 80.
 */
export const getPercentageColor = (percentage: number): string => {
  if (percentage >= 90) return 'text-green-600 bg-green-100';
  if (percentage >= 80) return 'text-yellow-600 bg-yellow-100';
  return 'text-red-600 bg-red-100';
};

/**
 * Formats Revel POS attribution data for display beneath a metric cell.
 * Returns null if there's nothing to display (no upsells).
 */
export const formatAttribution = (attr: RevelMetricAttribution | undefined): string | null => {
  if (!attr || attr.upsellCount === 0) return null;
  if (attr.purchaseCount > 0) {
    return `${attr.upsellCount} upsell${attr.upsellCount !== 1 ? 's' : ''}, ${attr.purchaseCount} purchase${attr.purchaseCount !== 1 ? 's' : ''}, $${attr.totalValue.toFixed(2)}`;
  }
  return `${attr.upsellCount} upsell${attr.upsellCount !== 1 ? 's' : ''}, 0 purchases`;
};

/**
 * Formats Revel POS attribution from raw counts (used in detail views
 * where data comes as separate fields rather than RevelMetricAttribution).
 * Returns null if there's nothing to display (no upsells).
 */
export const formatAttributionFromCounts = (
  upsellCount: number,
  purchaseCount: number,
  totalValue: number
): string | null => {
  if (upsellCount === 0) return null;
  if (purchaseCount > 0) {
    return `${upsellCount} upsell${upsellCount !== 1 ? 's' : ''}, ${purchaseCount} purchase${purchaseCount !== 1 ? 's' : ''}, $${totalValue.toFixed(2)}`;
  }
  return `${upsellCount} upsell${upsellCount !== 1 ? 's' : ''}, 0 purchases`;
};
